import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { withSpacing, clsx, safeRest } from '@wds/utils';
import { createMemoStyles } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStyles = createMemoStyles(function (_a) {
  var spacing = _a.spacing,
    border = _a.border,
    radius = _a.radius,
    shadow = _a.shadow,
    palette = _a.palette,
    type = _a.type;
  var section = {
    borderRadius: radius.small,
    boxShadow: shadow.none,
    border: border.muted,
    padding: spacing[5],
    bgColor: palette.background.light
  };
  return {
    wrenchSection: function () {
      return {
        boxSizing: 'border-box',
        fontFamily: type.fontFamily.base,
        fontSize: type.fontSize.body,
        '-webkit-font-smoothing': 'antialiased',
        backgroundColor: section.bgColor,
        border: section.border,
        borderRadius: section.borderRadius,
        boxShadow: section.boxShadow
      };
    },
    wrenchSectionHeader: {
      borderBottom: function (_a) {
        var noRule = _a.noRule;
        return noRule ? 'none' : [border.muted];
      },
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: '0',
      padding: [spacing[5]],
      minHeight: '72px',
      fontSize: type.fontSize.cardTitle,
      fontWeight: type.fontWeight.regular
    },
    wrenchSectionBody: function () {
      return {
        padding: section.padding
      };
    },
    wrenchSectionAddon: function () {
      return {
        display: 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        minHeight: '40px',
        margin: [0, 0, 'auto', spacing[4]],
        float: 'right'
      };
    }
  };
});

var Section = function Section(_a) {
  var children = _a.children,
    title = _a.title,
    _b = _a.padding,
    padding = _b === void 0 ? true : _b,
    addOn = _a.addOn,
    _c = _a.noRule,
    noRule = _c === void 0 ? false : _c,
    spacingClass = _a.spacingClass,
    rest = __rest(_a, ["children", "title", "padding", "addOn", "noRule", "spacingClass"]);
  var _d = useStyles({
      noRule: noRule
    }),
    wrenchSection = _d.wrenchSection,
    wrenchSectionBody = _d.wrenchSectionBody,
    wrenchSectionHeader = _d.wrenchSectionHeader,
    wrenchSectionAddon = _d.wrenchSectionAddon;
  var sectionAddon = jsx("div", {
    className: wrenchSectionAddon,
    children: addOn
  });
  var hadAddOn = !title && addOn && sectionAddon;
  var wrenchSectionWrapper = clsx(wrenchSection, spacingClass);
  return jsxs("div", __assign({
    className: wrenchSectionWrapper
  }, safeRest(rest), {
    children: [title && jsxs("header", {
      className: wrenchSectionHeader,
      children: [title, addOn && sectionAddon]
    }), padding ? jsxs("div", {
      className: wrenchSectionBody,
      children: [hadAddOn, children]
    }) : jsxs(Fragment, {
      children: [hadAddOn, children]
    })]
  }));
};
var SectionWithSpacing = withSpacing(Section);

export { SectionWithSpacing as Section };
